import {
  Box,
  Flex,
  Icon,
  IconButton,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
  Image,
} from '@chakra-ui/react'
import React, { useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaRegFolder } from 'react-icons/fa'
import { FiCopy, FiEdit, FiUser } from 'react-icons/fi'
import { GoVersions } from 'react-icons/go'
import { MdOutlineShare } from 'react-icons/md'
import { PiBracketsCurlyBold } from 'react-icons/pi'
import { checkOperationPermission, PermissionsEnum } from '../../../../utils/permissions'
import { ACLPermissionEnum } from '../../../constants'
import type {
  FileDetailsResponse,
  MediaLibrarySearchCollectionResponse,
  MediaLibrarySearchFileResponse,
  MediaLibrarySearchFolderResponse,
} from '../../../interfaces'
import { MLContext, UserContext } from '../../../providers'
import { PublicLinksInfoContext } from '../../../providers/PublicLinksInfoProvider'
import CopyTooltip from '../../CopyTooltip'
import PermissionGuard from '../../PermissionGuard'
import { RenameFileModal, ShareMediaCollectionModal } from '../MediaOperations'
import ShareAssetModal from '../MediaOperations/AccessControl/ShareAsset'
import CollectionRightSidebar from '../MediaOperations/CollectionRightSidebar'
import FolderRightSidebar from '../MediaOperations/FolderRightSidebar'
import MLRightSidebar from './MLRightSidebar'

type FileAsset = FileDetailsResponse | MediaLibrarySearchFileResponse
type FolderAsset = MediaLibrarySearchFolderResponse
type CollectionAsset = MediaLibrarySearchCollectionResponse

function isFileAsset(
  a: FileAsset | FolderAsset | CollectionAsset,
): a is FileAsset {
  return typeof (a as any).fileId === 'string'
}

function isFolderAsset(
  a: FileAsset | FolderAsset | CollectionAsset,
): a is FolderAsset {
  return (a as any).type === 'folder'
}

function isCollectionAsset(
  a: FileAsset | FolderAsset | CollectionAsset,
): a is CollectionAsset {
  return typeof (a as any).mediaCollectionId === 'string'
}

const PreviewEmptyState: React.FC = () => {
  const { t } = useTranslation()
  return (
    <Flex
      height="full"
      borderLeft="solid 1px"
      borderColor="imagekitGray.300"
      align="center"
      justify="center"
      px="6"
      textAlign="center"
    >
      <VStack align="center">
        <Image
          src="https://ik.imagekit.io/ikmedia/Search%20preview.svg"
          alt=""
          aria-hidden="true"
        />
        <Text color="imagekitGray.700">
          {t(
            'select_single_item_to_preview',
            'Select a single file, folder, or collection to preview details.',
          )}
        </Text>
      </VStack>
    </Flex>
  )
}

interface PreviewPanelProps {
  isDetailPage: boolean
  fileDetail?: FileDetailsResponse | MediaLibrarySearchFileResponse
  setFileDetails?: React.Dispatch<
    React.SetStateAction<FileDetailsResponse | undefined>
  >
}

const PreviewPanel: React.FC<PreviewPanelProps> = ({
  isDetailPage,
  fileDetail,
  setFileDetails,
}) => {
  const { t } = useTranslation()
  const { isPublicLink } = useContext(PublicLinksInfoContext)
  const [{ selectedFiles, selectedFolders, selectedMediaCollections }] =
    useContext(MLContext)
  const { user } = useContext(UserContext)

  const { totalSelected, file, folder, collection } = useMemo(() => {
    if (fileDetail) {
      return {
        totalSelected: 1,
        file: fileDetail as FileAsset,
        folder: undefined as FolderAsset | undefined,
        collection: undefined as CollectionAsset | undefined,
      }
    }

    const files = (selectedFiles ?? []) as FileAsset[]
    const folders = (selectedFolders ?? []) as FolderAsset[]
    const collections = (selectedMediaCollections ?? []) as CollectionAsset[]

    return {
      totalSelected: files.length + folders.length + collections.length,
      file: files.length === 1 ? files[0] : undefined,
      folder: folders.length === 1 ? folders[0] : undefined,
      collection: collections.length === 1 ? collections[0] : undefined,
    }
  }, [fileDetail, selectedFiles, selectedFolders, selectedMediaCollections])

  const [tabIndex, setTabIndex] = useState(0)

  const {
    isOpen: isOpenRenameFileModal,
    onOpen: onOpenRenameFileModal,
    onClose: onCloseRenameFileModal,
  } = useDisclosure()

  const {
    isOpen: isOpenShare,
    onOpen: onOpenShare,
    onClose: onCloseShare,
  } = useDisclosure()

  const {
    isOpen: isOpenShareMediaCollectionModal,
    onOpen: onOpenShareMediaCollectionModal,
    onClose: onCloseShareMediaCollectionModal,
  } = useDisclosure()

  const canManageFolder = useMemo(
    () =>
      folder
        ? checkOperationPermission({
            user,
            aclPermission: [ACLPermissionEnum.MANAGE],
            selectedFiles: [],
            selectedFolders: [folder],
            selectedMediaCollections: [],
          })
        : false,
    [user, folder],
  )

  const canManageCollection = useMemo(
    () =>
      collection
        ? checkOperationPermission({
            user,
            aclPermission: [ACLPermissionEnum.MANAGE],
            selectedFiles: [],
            selectedFolders: [],
            selectedMediaCollections: [collection],
          })
        : false,
    [user, collection],
  )

  if (totalSelected > 1 || totalSelected === 0) return <PreviewEmptyState />

  if (file && isFileAsset(file)) {
    const current = file

    return (
      <>
        <Flex
          height="full"
          borderLeft="solid 1px"
          borderColor="imagekitGray.300"
          direction="column"
        >
          <Box p="16px" borderBottom="solid 1px" borderColor="imagekitGray.300">
            <Flex align="center" justify="space-between" mb="7.5px" minW="0">
              <Tooltip label={current.name} placement="top-start" gutter={2}>
                <Text fontWeight="500" noOfLines={1} flex="1" minW="0">
                  {current.name}
                </Text>
              </Tooltip>
              <Flex gap="16px" flexShrink={0}>
                <CopyTooltip
                  label={t('copy_name', 'Copy name')}
                  successLabel={t('copied2')}
                  copyValue={current.name}
                  placement="top-start"
                  gutter={2}
                >
                  <IconButton
                    aria-label={t('copy_name', 'Copy name')}
                    variant="link"
                    icon={<FiCopy color="#a0aec0" />}
                    minW="20px"
                  />
                </CopyTooltip>

                {!isPublicLink && (
                  <PermissionGuard
                    permission={
                      PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE
                    }
                  >
                    {(hasPermission: boolean) =>
                      hasPermission ? (
                        <Tooltip
                          label={t('rename_file', 'Rename file')}
                          gutter={2}
                        >
                          <IconButton
                            aria-label={t('rename_file', 'Rename file')}
                            variant="link"
                            icon={<FiEdit color="#a0aec0" />}
                            minW="20px"
                            onClick={onOpenRenameFileModal}
                          />
                        </Tooltip>
                      ) : (
                        <></>
                      )
                    }
                  </PermissionGuard>
                )}
              </Flex>
            </Flex>

            {!isPublicLink && (
              <Flex
                fontSize="12px"
                color="#6e8198"
                gap="4px"
                direction="column"
              >
                {'filePath' in current && current.filePath ? (
                  <CopyTooltip
                    label={current.filePath}
                    successLabel={t('copied2')}
                    copyValue={current.filePath}
                    placement="top-start"
                    gutter={2}
                  >
                    <Flex
                      cursor="pointer"
                      align="center"
                      gap="12px"
                      width="100%"
                      minW="0"
                    >
                      <Icon minW="16px" as={FaRegFolder} />
                      <Text noOfLines={1} flex="1" minW="0">
                        {current.filePath}
                      </Text>
                    </Flex>
                  </CopyTooltip>
                ) : null}

                {(current as any)?.createdBy?.name ? (
                  <Tooltip
                    label={(current as any).createdBy.name}
                    placement="top-start"
                    gutter={2}
                  >
                    <Flex cursor="pointer" align="center" gap="12px" minW="0">
                      <Icon minW="16px" as={FiUser} />
                      <Text noOfLines={1} flex="1" minW="0">
                        {(current as any).createdBy.name}
                      </Text>
                    </Flex>
                  </Tooltip>
                ) : null}

                <CopyTooltip
                  label={t('file_id', 'File ID')}
                  successLabel={t('copied2')}
                  copyValue={current.fileId}
                  placement="top-start"
                  gutter={2}
                >
                  <Flex cursor="pointer" align="center" gap="12px">
                    <Icon minW="16px" as={PiBracketsCurlyBold} />
                    <Text>{current.fileId}</Text>
                  </Flex>
                </CopyTooltip>

                <CopyTooltip
                  label={t('version_id', 'Version ID')}
                  successLabel={t('copied2')}
                  copyValue={current.versionInfo.id}
                  placement="top-start"
                  gutter={2}
                >
                  <Flex cursor="pointer" align="center" gap="12px">
                    <Icon minW="16px" as={GoVersions} />
                    <Text>{current.versionInfo.id}</Text>
                  </Flex>
                </CopyTooltip>
              </Flex>
            )}
          </Box>

          <MLRightSidebar
            fileDetails={current as FileDetailsResponse}
            isDetailPage={isDetailPage}
          />
        </Flex>

        {isOpenRenameFileModal && (
          <RenameFileModal
            fileDetails={fileDetail}
            setFileDetails={setFileDetails}
            isOpen={isOpenRenameFileModal}
            onClose={onCloseRenameFileModal}
          />
        )}
      </>
    )
  }

  if (folder && isFolderAsset(folder)) {
    const headerName = folder.name
    const pathLabel = folder.folderPath
    const idValue = folder.folderId

    return (
      <Flex
        height="full"
        borderLeft="solid 1px"
        borderColor="imagekitGray.300"
        direction="column"
      >
        <Box p="16px" borderBottom="solid 1px" borderColor="imagekitGray.300">
          <Flex align="center" justify="space-between" mb="7.5px" minW="0">
            <Tooltip label={headerName} placement="top-start" gutter={2}>
              <Text fontWeight="500" noOfLines={1} flex="1" minW="0">
                {headerName}
              </Text>
            </Tooltip>
            <Flex gap="16px" flexShrink={0}>
              <CopyTooltip
                label={t('copy_name', 'Copy name')}
                successLabel={t('copied2')}
                copyValue={headerName}
                placement="top-start"
                gutter={2}
              >
                <IconButton
                  aria-label={t('copy_name', 'Copy name')}
                  variant="link"
                  icon={<FiCopy color="#a0aec0" />}
                  minW="20px"
                />
              </CopyTooltip>

              {canManageFolder && (
                <Tooltip label={t('share', 'Share')} gutter={2}>
                  <IconButton
                    aria-label={t('share', 'Share')}
                    variant="link"
                    icon={<MdOutlineShare color="#a0aec0" />}
                    minW="20px"
                    onClick={onOpenShare}
                  />
                </Tooltip>
              )}
            </Flex>
          </Flex>

          <Flex fontSize="12px" color="#6e8198" gap="4px" direction="column">
            <CopyTooltip
              label={pathLabel}
              successLabel={t('copied2')}
              copyValue={pathLabel}
              placement="top-start"
              gutter={2}
            >
              <Flex
                cursor="pointer"
                align="center"
                gap="12px"
                width="100%"
                minW="0"
              >
                <Icon minW="16px" as={FaRegFolder} />
                <Text noOfLines={1} flex="1" minW="0">
                  {pathLabel}
                </Text>
              </Flex>
            </CopyTooltip>

            <CopyTooltip
              label={t('folder_id', 'Folder ID')}
              successLabel={t('copied2')}
              copyValue={idValue}
              placement="top-start"
              gutter={2}
            >
              <Flex cursor="pointer" align="center" gap="12px">
                <Icon minW="16px" as={PiBracketsCurlyBold} />
                <Text>{idValue}</Text>
              </Flex>
            </CopyTooltip>
          </Flex>
        </Box>

        <FolderRightSidebar folder={folder} />

        <ShareAssetModal
          isOpen={isOpenShare}
          onClose={onCloseShare}
          assetId={folder.folderId}
          assetName={headerName}
          assetType="folder"
        />
      </Flex>
    )
  }

  if (collection && isCollectionAsset(collection)) {
    const headerName = collection.name
    const idValue = collection.mediaCollectionId
    const createdBy = (collection as any)?.createdBy?.name as string | undefined

    return (
      <Flex
        height="full"
        borderLeft="solid 1px"
        borderColor="imagekitGray.300"
        direction="column"
      >
        <Box p="16px" borderBottom="solid 1px" borderColor="imagekitGray.300">
          <Flex align="center" justify="space-between" mb="7.5px" minW="0">
            <Tooltip label={headerName} placement="top-start" gutter={2}>
              <Text fontWeight="500" noOfLines={1} flex="1" minW="0">
                {headerName}
              </Text>
            </Tooltip>
            <Flex gap="16px" flexShrink={0}>
              <CopyTooltip
                label={t('copy_name', 'Copy name')}
                successLabel={t('copied2')}
                copyValue={headerName}
                placement="top-start"
                gutter={2}
              >
                <IconButton
                  aria-label={t('copy_name', 'Copy name')}
                  variant="link"
                  icon={<FiCopy color="#a0aec0" />}
                  minW="20px"
                />
              </CopyTooltip>

              {canManageCollection && (
                <Tooltip label={t('share', 'Share')} gutter={2}>
                  <IconButton
                    aria-label={t('share', 'Share')}
                    variant="link"
                    icon={<MdOutlineShare color="#a0aec0" />}
                    minW="20px"
                    onClick={onOpenShareMediaCollectionModal}
                  />
                </Tooltip>
              )}
            </Flex>
          </Flex>

          <Flex fontSize="12px" color="#6e8198" gap="4px" direction="column">
            {createdBy ? (
              <Tooltip label={createdBy} placement="top-start" gutter={2}>
                <Flex cursor="pointer" align="center" gap="12px" minW="0">
                  <Icon minW="16px" as={FiUser} />
                  <Text noOfLines={1} flex="1" minW="0">
                    {createdBy}
                  </Text>
                </Flex>
              </Tooltip>
            ) : null}

            <CopyTooltip
              label={t('collection_id', 'Collection ID')}
              successLabel={t('copied2')}
              copyValue={idValue}
              placement="top-start"
              gutter={2}
            >
              <Flex cursor="pointer" align="center" gap="12px">
                <Icon minW="16px" as={PiBracketsCurlyBold} />
                <Text>{idValue}</Text>
              </Flex>
            </CopyTooltip>
          </Flex>
        </Box>
        <CollectionRightSidebar mediaCollection={collection} />

        {isOpenShareMediaCollectionModal && (
          <ShareMediaCollectionModal
            isOpen={isOpenShareMediaCollectionModal}
            onClose={onCloseShareMediaCollectionModal}
            assetId={collection.mediaCollectionId}
            assetName={collection.name}
          />
        )}
      </Flex>
    )
  }

  return null
}

export default PreviewPanel
